import {
  DESCRIPTION_MAX_LENGTH,
  NAME_MAX_LENGTH,
  NAME_MIN_LENGTH,
  SOURCE_TOOL_MAX_LENGTH,
  type CreatePrototypePayload,
  type UpdatePrototypePayload,
} from '@atlas/api-contract';
import { z } from 'zod';

import { isApiError } from '@/lib/api/errors';

/**
 * The prototype form, mirroring the server's `CreatePrototypeDto`.
 *
 * Bounds come from `@atlas/api-contract`, so the client cannot start accepting a
 * name the server will reject, and the behaviour is mirrored too: trim first,
 * then measure, and fold an empty optional field to `null` rather than storing
 * two spellings of "not set".
 *
 * Uniqueness is deliberately not mirrored — it is per-project and only the
 * database knows. The 409 is the answer, surfaced on the field that caused it.
 */
export const prototypeFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(NAME_MIN_LENGTH, 'Give the prototype a name.')
    .max(NAME_MAX_LENGTH, `Use ${String(NAME_MAX_LENGTH)} characters or fewer.`),
  description: z
    .string()
    .trim()
    .max(
      DESCRIPTION_MAX_LENGTH,
      `Use ${String(DESCRIPTION_MAX_LENGTH)} characters or fewer.`,
    ),
  sourceTool: z
    .string()
    .trim()
    .max(
      SOURCE_TOOL_MAX_LENGTH,
      `Use ${String(SOURCE_TOOL_MAX_LENGTH)} characters or fewer.`,
    ),
});

export type PrototypeFormValues = z.infer<typeof prototypeFormSchema>;

/** Empty form state, so create always opens clean. */
export const EMPTY_PROTOTYPE_FORM: PrototypeFormValues = {
  name: '',
  description: '',
  sourceTool: '',
};

/** An absent optional string is `null`, never `''` (matching the server's DTO). */
const toNullable = (value: string): string | null =>
  value.trim() === '' ? null : value.trim();

export const toCreatePrototypePayload = (
  values: PrototypeFormValues,
): CreatePrototypePayload => ({
  name: values.name.trim(),
  description: toNullable(values.description),
  sourceTool: toNullable(values.sourceTool),
});

export const toUpdatePrototypePayload = (
  values: PrototypeFormValues,
): UpdatePrototypePayload => ({
  name: values.name.trim(),
  description: toNullable(values.description),
  sourceTool: toNullable(values.sourceTool),
});

/**
 * What the name field says when the server rejects the name as taken.
 *
 * Written here rather than taken from the response, and scoped to the project:
 * the same prototype name in a different project is fine, and a message that
 * did not say so would read as a global rule that does not exist.
 */
export const DUPLICATE_PROTOTYPE_NAME_MESSAGE =
  'This project already has a prototype with that name. Pick a different one.';

/** Whether a failure is the unique-name conflict, and belongs on the field. */
export const isDuplicateNameError = (error: unknown): boolean =>
  isApiError(error) && error.status === 409;
